import { Button, Chip, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@nextui-org/react'
import { useSnackbar } from 'notistack'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  MdRoute, MdAutoAwesome, MdPlace, MdInventory2, MdPerson, MdPhone,
  MdFileOpen, MdStorefront, MdWarehouse, MdOpenInNew, MdMap, MdLocalShipping
} from 'react-icons/md'
import { useSelector } from 'react-redux'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import LogiTable from '../LogiTable'
import SectionShell from '../SectionShell'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { suppliesApi, SupplyRouteWorklist, SupplyRouteStopDto, RouteSuggestion } from '../../api'
import { TERMINO_ETIQUETA, TERMINO_ETIQUETA_LOWER } from '../../constants'
import { IAppStore } from '../../../../../models/store.model'

const SOLID = '#37417f'
const FOUNDATION = '#151930'

const mapsSearchUrl = (direccion: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccion)}`

const mapsEmbedUrl = (direccion: string): string =>
  `https://maps.google.com/maps?q=${encodeURIComponent(direccion)}&z=15&output=embed`

/* Timeline vertical de la ruta · Bodega → paradas (en orden) → Bodega */
const RouteTimeline: FC<{ paradas: SupplyRouteStopDto[], origen?: string }> = ({ paradas, origen = 'Bodega central' }) => {
  type Node =
    | { kind: 'origen', title: string, subtitle: string }
    | { kind: 'stop', n: number, title: string, subtitle: string, contenido: string }
  const nodes: Node[] = [
    { kind: 'origen', title: origen, subtitle: 'Inicio de ruta' },
    ...paradas.map((p, i): Node => ({ kind: 'stop', n: i + 1, title: p.store, subtitle: p.direccion_corta, contenido: p.contenido })),
    { kind: 'origen', title: origen, subtitle: 'Regreso a bodega' }
  ]

  return (
    <ol className='relative'>
      {nodes.map((nd, idx) => {
        const isLast = idx === nodes.length - 1
        const isOrigen = nd.kind === 'origen'
        return (
          <li key={idx} className='relative pl-11 pb-4 last:pb-0'>
            {!isLast && <span className='absolute left-[15px] top-8 bottom-0 w-[2px] bg-[#e2e5f0]' />}
            <span
              className='absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold'
              style={{ background: isOrigen ? FOUNDATION : SOLID }}
            >
              {isOrigen ? <MdWarehouse className='text-base' /> : nd.n}
            </span>
            <div className='min-h-[2rem] flex flex-col justify-center'>
              <div className='text-sm font-medium text-[#151930] leading-tight'>{nd.title}</div>
              <div className='text-[14px] text-[#151930]/55 leading-tight'>
                {nd.subtitle}
                {nd.kind === 'stop' && <span className='text-[#151930]/40'> · {nd.contenido}</span>}
              </div>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

const columns = [
  { name: '#', uid: 'orden', sortable: false },
  { name: 'Tienda', uid: 'store' },
  { name: 'Dirección', uid: 'direccion', sortBy: (p: SupplyRouteStopDto) => p.direccion_corta },
  { name: 'Contenido', uid: 'contenido', sortBy: (p: SupplyRouteStopDto) => p.piezas },
  { name: 'Estado', uid: 'estado' },
  { name: 'Tracking #', uid: 'tracking', sortBy: (p: SupplyRouteStopDto) => p.tracking_number },
  { name: TERMINO_ETIQUETA, uid: 'etiqueta', sortable: false },
  { name: '', uid: 'actions' }
]

const RepartoInternoView: FC = () => {
  const idSeller = useSelectedSeller()
  const userId = useSelector((s: IAppStore) => s.user?.id ?? null)
  const { enqueueSnackbar } = useSnackbar()
  const [suggestion, setSuggestion] = useState<RouteSuggestion | null>(null)
  const [suggesting, setSuggesting] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const [selected, setSelected] = useState<SupplyRouteStopDto | null>(null)

  const wl = useApi<SupplyRouteWorklist>(
    useCallback(async () => await suppliesApi.internalRoute(idSeller), [idSeller]),
    [idSeller]
  )

  // Paradas en el orden sugerido por IA (si hay), o el orden natural.
  const paradas = useMemo(() => {
    const base = wl.data?.paradas ?? []
    if (suggestion === null || suggestion.orden.length === 0) return base
    const byId = new Map(base.map((p) => [p.id_shipment, p]))
    const ordered: SupplyRouteStopDto[] = []
    for (const id of suggestion.orden) {
      const p = byId.get(id)
      if (p !== undefined) { ordered.push(p); byId.delete(id) }
    }
    for (const p of byId.values()) ordered.push(p)
    return ordered
  }, [wl.data, suggestion])

  const handleSuggest = async (): Promise<void> => {
    setSuggesting(true)
    try {
      const r = await suppliesApi.suggestRoute(idSeller)
      setSuggestion(r)
      enqueueSnackbar(r.fallback ? 'Ruta ordenada (sin IA disponible)' : 'Ruta sugerida por IA', { variant: r.fallback ? 'info' : 'success' })
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo sugerir la ruta', { variant: 'error' })
    } finally { setSuggesting(false) }
  }

  const handleMark = async (idShipment: number, tipo: 'salida' | 'entrega'): Promise<void> => {
    setBusy(idShipment)
    try {
      const r = await suppliesApi.markInternal(idShipment, tipo, idSeller, userId === 0 ? null : userId)
      enqueueSnackbar(
        tipo === 'entrega'
          ? (r.recepcion_aplicada === true ? 'Entrega registrada · insumos sumados al inventario' : 'El envío ya había sido recibido')
          : 'Envío marcado en ruta',
        { variant: 'success' }
      )
      setSelected(null)
      setSuggestion(null)
      await wl.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo registrar', { variant: 'error' })
    } finally { setBusy(null) }
  }

  const data = wl.data

  const renderCell = (p: SupplyRouteStopDto, columnKey: string): JSX.Element => {
    const orden = paradas.findIndex((x) => x.id_shipment === p.id_shipment) + 1
    switch (columnKey) {
      case 'orden':
        return <span className='text-xs font-semibold text-[#151930]/60'>{orden}</span>
      case 'store':
        return <span className='font-medium text-xs text-[#151930]'>{p.store}</span>
      case 'direccion':
        return <span className='text-xs text-[#151930]/70'>{p.direccion_corta}</span>
      case 'contenido':
        return <span className='text-xs text-[#151930]/70'>{p.contenido}</span>
      case 'estado':
        return p.estado === 'en_ruta'
          ? <Chip size='sm' variant='flat' color='primary'>En camino</Chip>
          : <Chip size='sm' variant='flat' color='warning'>Creada</Chip>
      case 'tracking':
        return (
          <span className=' text-xs'>
            {p.tracking_number ?? <span className='text-gray-400'>—</span>}
          </span>
        )
      case 'etiqueta':
        return (
          <a
            className='text-[#4b75f7] font-medium hover:underline text-xs inline-flex items-center gap-1'
            href={suppliesApi.supplyLabelUrl(p.id_shipment, idSeller)}
            target='_blank'
            rel='noreferrer'
          >
            <MdFileOpen className='text-sm' /> Ver {TERMINO_ETIQUETA_LOWER}
          </a>
        )
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            <Button size='sm' className='bg-black text-white text-xs h-8' onPress={() => setSelected(p)}>
              Ver detalle
            </Button>
          </div>
        )
      default:
        return <span className='text-xs'>{(p as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      <BackButton to='insumos' label='Volver a Insumos' />

      {/* Header + totales + sugerir ruta */}
      <div className='flex items-start justify-between gap-3 flex-wrap'>
        <div>
          <h2 className='text-lg font-semibold text-[#151930]'>Reparto Reuse de insumos</h2>
          <p className='text-xs text-[#151930]/60 mt-0.5'>
            {data !== null
              ? `${data.total_paradas} paradas · ${data.total_piezas} piezas · ${data.por_enviar} por enviar · ${data.en_ruta} en ruta`
              : 'Envíos Reuse por repartir a tiendas'}
          </p>
        </div>
        <Button
          size='sm'
          className='bg-[#151930] text-white text-xs h-9'
          startContent={<MdRoute className='text-base' />}
          isLoading={suggesting}
          isDisabled={(data?.total_paradas ?? 0) === 0}
          onPress={() => { void handleSuggest() }}
        >
          Sugerir ruta con IA
        </Button>
      </div>

      {/* Panel de ruta sugerida · timeline visual + Maps */}
      {suggestion !== null && suggestion.orden.length > 0 && (
        <div className='rounded-2xl border border-[#CECBF6] bg-[#EEEDFE] overflow-hidden'>
          <div className='px-4 py-3 flex items-start justify-between gap-3 flex-wrap border-b border-[#d8d4f3]'>
            <div>
              <div className='flex items-center gap-2 text-xs font-semibold text-[#3C3489]'>
                <MdAutoAwesome className='text-sm' />
                {suggestion.fallback ? 'Orden de ruta' : 'Ruta sugerida por IA'}
              </div>
              <div className='text-sm text-[#26215C] mt-0.5'>{suggestion.resumen}</div>
              {(suggestion.distancia_km !== null || suggestion.duracion_min !== null) && (
                <div className='text-xs text-[#534AB7] mt-0.5'>
                  {suggestion.distancia_km !== null ? `~${suggestion.distancia_km} km` : ''}
                  {suggestion.distancia_km !== null && suggestion.duracion_min !== null ? ' · ' : ''}
                  {suggestion.duracion_min !== null ? `~${suggestion.duracion_min} min` : ''}
                </div>
              )}
            </div>
            {suggestion.maps_url !== '' && (
              <Button
                as='a' href={suggestion.maps_url} target='_blank' rel='noreferrer'
                size='sm' className='bg-[#37417f] text-white text-xs h-8'
                startContent={<MdOpenInNew className='text-sm' />}
              >
                Ver en Google Maps
              </Button>
            )}
          </div>
          <div className='bg-white px-4 py-4'>
            <RouteTimeline paradas={paradas} />
          </div>
        </div>
      )}

      {/* Grilla de paradas */}
      <SectionShell title='Reparto Reuse de insumos' icon={MdRoute} noPadding>
        <div className='px-5 pb-5 pt-2'>
          <ApiStateView
            loading={wl.loading} error={wl.error} onRetry={wl.reload}
            empty={!wl.loading && paradas.length === 0}
            emptyText='No hay envíos Reuse por repartir'
          >
            <LogiTable
              ariaLabel='Reparto Reuse de insumos'
              columns={columns}
              items={paradas}
              rowKey={(p: SupplyRouteStopDto) => p.id_shipment}
              renderCell={renderCell}
              loading={wl.loading}
              emptyText='No hay envíos Reuse por repartir'
              rowsPerPage={10}
              bottomExtra={<div className='text-xs text-[#151930]/60'>{paradas.length} paradas</div>}
            />
          </ApiStateView>
        </div>
      </SectionShell>

      {/* Modal de detalle de parada (card + mapa) */}
      <StopModal
        stop={selected}
        idSeller={idSeller}
        busy={selected !== null && busy === selected.id_shipment}
        onClose={() => setSelected(null)}
        onSalida={() => { if (selected !== null) void handleMark(selected.id_shipment, 'salida') }}
        onEntrega={() => { if (selected !== null) void handleMark(selected.id_shipment, 'entrega') }}
      />
    </div>
  )
}

const StopModal: FC<{
  stop: SupplyRouteStopDto | null
  idSeller: number | null
  busy: boolean
  onClose: () => void
  onSalida: () => void
  onEntrega: () => void
}> = ({ stop, idSeller, busy, onClose, onSalida, onEntrega }) => {
  const [showMap, setShowMap] = useState(false)
  useEffect(() => { setShowMap(false) }, [stop?.id_shipment])

  if (stop === null) return null
  const enRuta = stop.estado === 'en_ruta'
  const etiquetaUrl = suppliesApi.supplyLabelUrl(stop.id_shipment, idSeller)

  return (
    <Modal isOpen={stop !== null} onClose={onClose} size='2xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex items-center justify-between gap-2 pr-10'>
          <div className='flex flex-col gap-0.5'>
            <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Parada · Reparto Reuse</span>
            <span className='text-base font-bold text-[#151930]'>{stop.store}</span>
          </div>
          {enRuta
            ? <Chip size='sm' variant='flat' color='primary'>En camino</Chip>
            : <Chip size='sm' variant='flat' color='warning'>Creada</Chip>}
        </ModalHeader>

        <ModalBody className='gap-4'>
          <div className='space-y-2'>
            <div className='flex items-start gap-2 text-sm text-[#151930]'>
              <MdPlace className='text-base mt-0.5 shrink-0' style={{ color: SOLID }} />
              <span>{stop.direccion}</span>
            </div>
            <div className='flex gap-4 text-xs text-[#151930]/60 flex-wrap'>
              <span className='inline-flex items-center gap-1'><MdInventory2 className='text-sm' /> {stop.contenido}</span>
              {stop.contacto_nombre !== null && <span className='inline-flex items-center gap-1'><MdPerson className='text-sm' /> {stop.contacto_nombre}</span>}
              {stop.contacto_telefono !== null && <span className='inline-flex items-center gap-1'><MdPhone className='text-sm' /> {stop.contacto_telefono}</span>}
            </div>
            {stop.referencia !== null && stop.referencia !== '' && (
              <div className='text-xs text-[#151930]/45'>Ref: {stop.referencia}</div>
            )}
          </div>

          {/* Mapa · solo se carga al pedirlo (botón "Ver en mapa" en el pie) */}
          {showMap && (
            <>
              <div className='rounded-xl overflow-hidden border border-[#eef0f7]'>
                <iframe
                  title={`Mapa · ${stop.store}`}
                  src={mapsEmbedUrl(stop.direccion)}
                  className='w-full'
                  style={{ height: 280, border: 'none' }}
                  loading='lazy'
                />
              </div>
              <a
                href={mapsSearchUrl(stop.direccion)} target='_blank' rel='noreferrer'
                className='inline-flex items-center gap-1 text-xs font-semibold text-[#37417f] hover:underline'
              >
                <MdOpenInNew className='text-sm' /> Abrir en Google Maps
              </a>
            </>
          )}
        </ModalBody>

        <ModalFooter className='flex flex-wrap items-center justify-end gap-2'>
          <Button
            variant='flat' className='text-xs h-9' startContent={<MdMap className='text-sm' />}
            onPress={() => setShowMap(v => !v)}
          >
            {showMap ? 'Ocultar mapa' : 'Ver en mapa'}
          </Button>
          <Button as='a' href={etiquetaUrl} target='_blank' rel='noreferrer'
            variant='flat' className='text-xs h-9' startContent={<MdFileOpen className='text-sm' />}>
            Ver {TERMINO_ETIQUETA_LOWER}
          </Button>
          {!enRuta
            ? (
              <Button className='bg-black text-white text-xs h-9' isLoading={busy}
                startContent={<MdLocalShipping className='text-sm' />} onPress={onSalida}>
                Marcar salida de bodega
              </Button>
              )
            : (
              <Button className='bg-black text-white text-xs h-9' isLoading={busy}
                startContent={<MdStorefront className='text-sm' />} onPress={onEntrega}>
                Marcar entrega en tienda
              </Button>
              )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default RepartoInternoView
